import { watchImmediate } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { useTheme } from '@silae/composables/ui';
import AgoIcon from '~/assets/ago-icon.svg';
import { AgoMetadata, AgoWidgetConfig } from '~/domain';
import { PlanType } from '~/services';
import { useAuthStore, useOrganizationStore, usePrimordialSoupStore } from '~/stores';

export function useOnlineHelp(): void {
	let tag: HTMLScriptElement;
	const { isDark } = useTheme();
	const { isAuthenticated, userProfile } = storeToRefs(useAuthStore());
	const { organization } = storeToRefs(useOrganizationStore());
	const { agoInfo } = storeToRefs(usePrimordialSoupStore());
	const { t } = useI18n();

	/** Define AGO metadata usage */
	const agoMetadataPayload = computed<AgoMetadata>(() =>
		userProfile.value && agoInfo.value
			? {
					login: userProfile.value.login,
					firstName: userProfile.value.firstName,
					lastName: userProfile.value.lastName,
					bookkeeperLabel: userProfile.value.organizationLabel,
					bookkeeperId: userProfile.value.organizationId,
					domaineGuid: userProfile.value.domainGuid,

					// Below 3 attributes might not be defined, depending on whether user has selected a company or not
					clientId: organization.value?.id,
					clientPlan: organization.value?.plan?.toLowerCase() as Lowercase<PlanType>,
					dossierGuid: organization.value?.dossierGuid
				}
			: undefined
	);

	/** allow us to hide AGO widget according to user authentication (cf: _layout.scss) */
	watchImmediate(isAuthenticated, newIsAuthenticated => {
		if (newIsAuthenticated != null && newIsAuthenticated) {
			document.body.classList.add('is-authenticated');
		} else {
			document.body.classList.remove('is-authenticated');
		}
	});

	watchImmediate(agoInfo, newAgoInfo => {
		if (newAgoInfo && !window.AGO) {
			/** Define base AGO config */
			window.AGO = {
				basepath: 'https://silae-expense.useago.com/',
				widgetApiKey: newAgoInfo.agoApiKey,
				title: t('ago.title'), //TODO: @Olivier: i18n
				prompt: t('ago.prompt'), //TODO: @Olivier: i18n
				icon: AgoIcon,
				colors: {
					button: '#056EA1',
					header: '#056EA1'
				},
				jwt: newAgoInfo.jwt
			} as AgoWidgetConfig;

			/** Load AGO script for future usage */
			tag = document.createElement('script');
			tag.id = 'ago-script';
			tag.src = 'https://useago.github.io/widgetjs/frame.js';
			tag.async = true;
			tag.crossOrigin = 'anonymous';

			const headElementTag = document.head;
			headElementTag?.appendChild(tag);

			/** CAREFUL HERE: following watchers are instantiated once AGO script is loaded */
			tag.onload = () => {
				watchImmediate(isDark, (newIsDark, oldIsDark) => {
					if (newIsDark != undefined && newIsDark !== oldIsDark) {
						const agoWrapper = document.getElementById('ago-wrapper');
						if (agoWrapper) {
							if (newIsDark) {
								agoWrapper.classList.add('dark');
								agoWrapper.classList.remove('light');
							} else {
								agoWrapper.classList.add('light');
								agoWrapper.classList.remove('dark');
							}
						}
					}
				});

				watchImmediate(
					() => ({
						agoMetadataPayload: agoMetadataPayload.value,
						isAuthenticated: isAuthenticated.value
					}),
					(newData, oldData) => {
						if (
							newData.isAuthenticated &&
							newData.agoMetadataPayload &&
							(!oldData?.agoMetadataPayload ||
								(oldData.agoMetadataPayload &&
									JSON.stringify(newData.agoMetadataPayload) !== JSON.stringify(oldData.agoMetadataPayload)))
						) {
							window.sendMetadataToAGO(newData.agoMetadataPayload);
						}
					}
				);
			};
		} else if (window.AGO && newAgoInfo) {
			/** In this case (e.g: deco/reco with different account), we update AGO auth jwt only */
			window.AGO.jwt = newAgoInfo.jwt;
		}
	});
}
